import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { setNodePositions } from '@store/reducers/treeGraphSlice';
import { Group } from '@visx/group';

import type { AppDispatch } from '@store/index';

import './TreeGraph.scss';

const NODE_RADIUS = 49;

interface Node {
  id: number;
  name: string;
  x: number;
  y: number;
}

interface INodePosition {
  name: string;
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
  x: number;
  y: number;
}

interface Link {
  type: string;
  source: Node;
  target: Node;
  direction: 'left' | 'right' | 'both';
}

interface ILink {
  data: [Node, Node];
  direction: 'left' | 'right' | 'both';
  color: string | string[];
}

interface ITreeGraphProps {
  connections: ILink[];
}

function calculateDistance(x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

function LinearGradientDefinition({
  data,
  direction,
  color,
}: {
  data: [Node, Node];
  direction: 'left' | 'right' | 'both';
  color: string | string[];
}) {
  let colors;
  if (typeof color === 'string') {
    colors = [color];
  } else {
    colors = color;
  }

  const distance = calculateDistance(
    data[0].x,
    data[0].y,
    data[1].x,
    data[1].y,
  );
  console.log('預留 distance prop for alternative UI effects', distance);

  const speed = 2;

  // 如果要速率相同就用 v
  // const v = 500 / distance;
  // const speed = v;

  const gradientRef = useRef<SVGLinearGradientElement | null>(null);

  let linearGradient;
  switch (direction) {
    case 'right': {
      linearGradient = (
        <linearGradient
          id={`linkGradientForward-${color.toString()}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
          spreadMethod="repeat"
          ref={gradientRef}
        >
          {colors.map((thisColor, idx) => {
            const portionStart = (idx * 100) / colors.length;
            const portionEnd = ((idx + 1) * 100) / colors.length;
            const subPortion = (portionEnd - portionStart) / 2;
            const offset1 = portionStart;
            const offset2 = portionStart + subPortion;
            const offset3 = portionEnd;
            return (
              <React.Fragment key={thisColor}>
                <stop
                  offset={`${offset1}%`}
                  style={{ stopColor: 'grey', stopOpacity: 0 }}
                />
                <stop
                  offset={`${offset2}%`}
                  style={{ stopColor: 'grey', stopOpacity: 0.5 }}
                />
                <stop
                  offset={`${offset3}%`}
                  style={{ stopColor: thisColor, stopOpacity: 1 }}
                />
              </React.Fragment>
            );
          })}
        </linearGradient>
      );
      break;
    }
    case 'left': {
      linearGradient = (
        <linearGradient
          id={`linkGradientBackward-${color.toString()}`}
          x1="100%"
          y1="0%"
          x2="0%"
          y2="0%"
          spreadMethod="repeat"
          ref={gradientRef}
        >
          {colors.map((thisColor, idx) => {
            const portionStart = (idx * 100) / colors.length;
            const portionEnd = ((idx + 1) * 100) / colors.length;
            const subPortion = (portionEnd - portionStart) / 2;
            const offset1 = portionStart;
            const offset2 = portionStart + subPortion;
            const offset3 = portionEnd;
            return (
              <React.Fragment key={thisColor}>
                <stop
                  offset={`${offset1}%`}
                  style={{ stopColor: 'grey', stopOpacity: 0 }}
                />
                <stop
                  offset={`${offset2}%`}
                  style={{ stopColor: 'grey', stopOpacity: 0.5 }}
                />
                <stop
                  offset={`${offset3}%`}
                  style={{ stopColor: thisColor, stopOpacity: 1 }}
                />
              </React.Fragment>
            );
          })}
        </linearGradient>
      );
      break;
    }
    case 'both': {
      linearGradient = (
        <linearGradient
          id={`linkGradientBoth-${color.toString()}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
          spreadMethod="repeat"
          ref={gradientRef}
        >
          {colors.map((thisColor, idx) => {
            const portionStart = (idx * 100) / colors.length;
            const portionEnd = ((idx + 1) * 100) / colors.length;
            const subPortion = (portionEnd - portionStart) / 2;
            const offset1 = portionStart;
            const offset2 = portionStart + subPortion;
            const offset3 = portionEnd;
            return (
              <React.Fragment key={thisColor}>
                <stop
                  offset={`${offset1}%`}
                  style={{ stopColor: 'grey', stopOpacity: 0 }}
                />
                <stop
                  offset={`${offset2}%`}
                  style={{ stopColor: thisColor, stopOpacity: 1 }}
                />
                <stop
                  offset={`${offset3}%`}
                  style={{ stopColor: 'grey', stopOpacity: 0 }}
                />
              </React.Fragment>
            );
          })}
        </linearGradient>
      );
      break;
    }
    default: {
      linearGradient = null;
    }
  }

  useEffect(() => {
    const gradient = gradientRef.current;
    let x1 = direction === 'left' ? 100 : 0;
    let x2 = direction === 'left' ? 0 : 100;
    let animationFrameId: number;
    let directionInterval = 0;

    function animateGradient() {
      switch (direction) {
        case 'right': {
          x1 += speed;
          x2 += speed;

          if (x1 > 100) {
            x1 = 0;
            x2 = 100;
          }

          break;
        }
        case 'left': {
          x1 -= speed;
          x2 -= speed;

          if (x1 < 0) {
            x1 = 100;
            x2 = 0;
          }
          break;
        }

        case 'both': {
          directionInterval += 1;
          if (Math.floor(directionInterval / 100) % 2 === 0) {
            x1 += speed;
            x2 += speed;

            if (x1 > 100) {
              x1 = 0;
              x2 = 100;
            }
          } else {
            x1 -= speed;
            x2 -= speed;
            if (x1 < 0) {
              x1 = 100;
              x2 = 0;
            }
          }
          break;
        }

        default: {
          break;
        }
      }

      if (gradient) {
        gradient.setAttribute('x1', `${x1}%`);
        gradient.setAttribute('x2', `${x2}%`);
        animationFrameId = requestAnimationFrame(animateGradient);
      }
    }
    animateGradient();

    return () => cancelAnimationFrame(animationFrameId);
  }, [gradientRef.current]);

  return linearGradient;
}

export default function TreeGraph({ connections }: ITreeGraphProps) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  function renderLink(item: Link, color: string | string[]) {
    const { source, target } = item;
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const angle = Math.atan2(dy, dx);
    const x1 = source.x + NODE_RADIUS * Math.cos(angle);
    const y1 = source.y + NODE_RADIUS * Math.sin(angle);
    const x2 = target.x - NODE_RADIUS * Math.cos(angle);
    const y2 = target.y - NODE_RADIUS * Math.sin(angle);

    let direction: string;
    if (item.direction === 'both') {
      direction = 'Both';
    } else if (item.direction === 'left') {
      direction = 'Backward';
    } else {
      direction = 'Forward';
    }

    return (
      <line
        className="link"
        x1={x2}
        y1={y2}
        x2={x1}
        y2={y1}
        stroke={`url(#linkGradient${direction}-${color.toString()})`}
        filter="url(#glow)"
      />
    );
  }

  function renderConnections() {
    const flattenedNodes = connections.reduce((acc, curr) => {
      acc.push(...curr.data);
      return acc;
    }, [] as Node[]);

    const uniqueNodes = flattenedNodes.reduce((acc: Node[], curr: Node) => {
      const existingNode = acc.find((node) => node.name === curr.name);
      if (!existingNode) {
        acc.push(curr);
      }
      return acc;
    }, [] as Node[]);

    const nodeComponents = uniqueNodes.map((node) => (
      <Group key={node.id} top={node.y} left={node.x} data-name={t(node.name)}>
        <circle className="node" r={NODE_RADIUS} />
        <foreignObject
          x={-NODE_RADIUS}
          y={-NODE_RADIUS / 2}
          width={NODE_RADIUS * 2}
          height={NODE_RADIUS}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              textAlign: 'center',
              wordWrap: 'break-word',
              color: 'white',
            }}
          >
            {t(node.name)}
          </div>
        </foreignObject>
      </Group>
    ));
    const linkComponents = connections.map((connection) =>
      connection.data.map((node, idx) => {
        const link = {
          type: 'link',
          source: connection.data[idx - 1]
            ? connection.data[idx - 1]
            : connection.data[idx],
          target: connection.data[idx],
          direction: connection.direction,
        };
        return (
          <React.Fragment key={node.id}>
            <g key={`${link.source.name}-${link.target.name}`}>
              {renderLink(link, connection.color)}
            </g>
          </React.Fragment>
        );
      }),
    );
    return (
      <>
        {nodeComponents}
        {linkComponents}
      </>
    );
  }

  useEffect(() => {
    function handleResize() {
      if (svgRef.current) {
        const nodeElements = svgRef.current.querySelectorAll('[data-name]');
        const nodePositions: INodePosition[] = [];

        nodeElements.forEach((nodeElement) => {
          const { top, right, bottom, left, width, height, x, y } =
            nodeElement.getBoundingClientRect();

          nodePositions.push({
            name: nodeElement.getAttribute('data-name') || '',
            bottom,
            height,
            left,
            right,
            top,
            width,
            x,
            y,
          });
        });

        dispatch(setNodePositions(nodePositions));
      }
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [svgRef.current]);

  return (
    <div className="TreeGraph">
      <svg ref={svgRef} viewBox="0 0 1101 790">
        <defs>
          {connections.map((connection) => (
            // using index or Math.random() as key will only be a problem if we are re-ordering the list.
            <LinearGradientDefinition
              data={connection.data}
              direction={connection.direction}
              color={connection.color}
              key={
                connection.data[0].name +
                connection.data[1].name +
                Math.random()
              }
            />
          ))}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* <filter id="glow" x="-50%" y="-50%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter> */}

          <filter id="blurLime">
            {/* <!-- Extract the lime color --> */}
            {/* <feComponentTransfer in="SourceGraphic" result="isolatedLime">
              <feFuncR type="discrete" tableValues="0 1" />
              <feFuncG type="discrete" tableValues="1 0" />
              <feFuncB type="discrete" tableValues="0 0" />
            </feComponentTransfer> */}

            {/* <!-- Blur the extracted lime color --> */}
            {/* <feGaussianBlur
              in="isolatedLime"
              stdDeviation="3"
              result="blurredLime"
            /> */}

            {/* <!-- Combine the blurred lime color with the original graphic --> */}
            {/* <feMerge>
              <feMergeNode in="blurredLime" />
              <feMergeNode in="SourceGraphic" />
            </feMerge> */}
          </filter>
        </defs>
        {renderConnections()}
      </svg>
    </div>
  );
}
